import { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button, Input, Space, Typography } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import * as Rules from 'admin/lib/ValidationRules'
import {
  Section,
  AutoCompleteFormItem,
  FormItem,
  FormRowWithValidation
} from 'admin/lib/Forms'
import { Undo } from 'admin/lib/Icons/Undo'

import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import {
  Waste as fields,
  IMPERIAL_VOLUME_UNITS,
  IMPERIAL_WEIGHT_UNITS,
  METRIC_VOLUME_UNITS,
  METRIC_WEIGHT_UNITS
} from 'admin/lib/Records'
import {
  CheckContainerType,
  CheckQuantity,
  CheckVolume,
  CheckWeight
} from 'admin/lib/SimpleDataValidation'
import {
  AddMultipleLineItemsModal,
  UnitSelector,
  useLineItemShortcuts
} from 'admin/pages/RecordEdit/common'
import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'
import { LineItemTitle } from 'admin/pages/RecordEdit_v2/common/styledComponents'

import { returnValueForLineItemField } from 'admin/pages/RecordEdit/common/returnValueForLineItemField'
import { cleanWasteCode } from 'admin/lib/CleaningRules'
import { KnownContainerTypePresetSelect } from './KnownContainerTypePresetSelect'

export function WasteLineItem({
  index,
  item,
  onDuplicate,
  onDuplicateMultiple,
  onRemove,
  setLineItemErrors,
  isImperial
}) {
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [productDetailsOpen, setProductDetailsOpen] = useState(true)
  const [isDuplicateItemsMultipleOpen, setIsDuplicateItemsMultipleOpen] =
    useState(false)

  const lineItem = useLineItemStore(state => state[index])
  const deleteLineItem = useLineItemStore(state => state.deleteKey)
  const duplicateLineItem = useLineItemStore(state => state.duplicateKey)
  const { moduleName, recordId } = useParams()

  const namePrefix = `uniquePrefix[${index}].`
  // Alias Used for autocomplete fields
  const aliasPrefix = `data.items[${index}].`

  const form = useForm({
    reValidateMode: 'onChange',
    mode: 'all'
  })

  const [name, setName] = useState(item.containerType)

  const { TextArea } = Input

  const handleWasteCodeChange = event => {
    const updatedValue = cleanWasteCode(event.target.value)
    form.setValue(event.target.name, updatedValue)

    const updatedEvent = { ...event }
    updatedEvent.target.value = updatedValue
    dataChange(event)
  }

  const dataChange = event => {
    setUnsavedChanges(true)
    setLineItemErrors(form.errors)

    const lineItemKey = event.target.name.split(namePrefix).pop()

    if (lineItemKey === 'containerType') {
      setName(event.target.value)
    }

    const newLineItem = {
      ...lineItem,
      [lineItemKey]:
        event.target.type === 'number'
          ? parseFloat(event.target.value)
          : event.target.value
    }
    useLineItemStore.setState({ [index]: newLineItem })

    // If UoM change also trigger a blur event for the parent input
    // so that we trigger any validation rules
    if (
      lineItemKey.includes('UoM') &&
      lineItem[lineItemKey] !== event.target.value
    ) {
      const selector = `[name='${event.target.name.split('UoM')[0]}']`
      setTimeout(() => {
        document.querySelector(selector).blur()
        document.querySelector(selector).focus()
      }, 1)
    }
  }

  const handleFocus = event => {
    const clipboardValue = sessionStorage.getItem('clipboard')
    sessionStorage.removeItem('clipboard')

    if (clipboardValue && clipboardValue !== '') {
      const updatedValue = returnValueForLineItemField(
        clipboardValue,
        event.target.type,
        event.target.value
      )
      form.setValue(event.target.name, updatedValue)

      const updatedEvent = { ...event }
      updatedEvent.target.value = updatedValue
      dataChange(updatedEvent)
    }
  }

  const openDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(true)
  }

  const closeDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(false)
  }

  const duplicate = event => {
    // Duplicate on state
    duplicateLineItem(index)
    // Duplicate on Parent
    onDuplicate(lineItem)
  }

  const duplicateMultiple = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        duplicate()
      }
    }
  }

  const handleDuplicate = HandleSingleAndDoubleClick(
    duplicate,
    openDuplicateItemMultipleModal
  )

  const handleRemove = event => {
    // if lineitem has id (which means it has been saved) just change the isDeleted flag
    // Otherwise remove the entire item
    if (lineItem.id) {
      // Create event and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: true
        }
      }
      dataChange(mockEvent)
    } else {
      // Remove from parent
      onRemove()
      // Remove from state
      deleteLineItem(index)
    }
  }

  const handleUndoRemove = event => {
    // Only shown to deleted products that have a real id
    if (lineItem.id) {
      // Create event and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: false
        }
      }
      dataChange(mockEvent)
    }
  }

  const handleWheelScrollInNumberField = event => {
    if (
      document.activeElement.type === 'number' &&
      document.activeElement.matches(':hover')
    ) {
      event.preventDefault()
    }
  }

  const handleKnownContainerTypeSelectChange = value => {
    // Create mock event and pass to dataChange function
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.knownContainerType.path,
        value: value
      }
    }
    dataChange(mockEvent)
  }

  const shortcuts = useLineItemShortcuts(handleRemove, duplicate)

  useEffect(() => {
    // If line Item has been changed but not saved and the 'Add New Line Item' button is pressed
    // Stop entered values from being overwritten with original values
    if (item && !unsavedChanges) {
      // Populate from record
      useLineItemStore.setState({ [index]: { ...item } })
    }
  }, [index, item, unsavedChanges])

  useEffect(() => {
    document.addEventListener('wheel', handleWheelScrollInNumberField, {
      passive: false
    })
  }, [])

  const handleProductOpenChange = () => {
    setProductDetailsOpen(!productDetailsOpen)
    sessionStorage.setItem(`${recordId}-${index}-open`, !productDetailsOpen)
  }

  useEffect(() => {
    const open = sessionStorage.getItem(`${recordId}-${index}-open`)
    if (open) {
      setProductDetailsOpen(open === 'true')
    }
  }, [index, recordId])

  if (!lineItem) {
    return null
  }

  if (lineItem.isDeleted) {
    return (
      <FormProvider {...form}>
        <Section
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <Space
              direction="horizontal"
              align="center"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Item'}
                </span>
                <br />
                Deleted
              </Typography.Text>

              <Button
                type="text"
                tabIndex={-1}
                icon={<Undo />}
                onClick={handleUndoRemove}
                title="Undo delete"
              />
            </Space>
          )}
        </Section>
      </FormProvider>
    )
  }

  if (lineItem.isRemoved) {
    return (
      <FormProvider {...form}>
        <Section
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <Space
              direction="horizontal"
              align="center"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Item'}
                </span>
                <br />
                Removed by client
              </Typography.Text>
            </Space>
          )}
        </Section>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...form}>
      <Section
        className={'LineItem'}
        onKeyDown={shortcuts.onKeyDown}
        colour="alt"
      >
        {({ firstInputRef }) => (
          <details
            {...(productDetailsOpen ? { open: true } : {})}
            style={{ position: 'relative' }}
          >
            <LineItemTitle
              style={{ marginTop: '0' }}
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{ width: '272px' }}
                onClick={handleProductOpenChange}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                {name || 'New Item'}
              </Typography.Text>

              <ButtonGroup>
                <Button
                  type="text"
                  tabIndex="-1"
                  icon={<Icons.CopyOutlined />}
                  onClick={handleDuplicate}
                  title="Duplicate"
                />
                <Button
                  type="text"
                  tabIndex="-1"
                  danger
                  icon={<Icons.DeleteOutlined />}
                  onClick={handleRemove}
                  title="Delete"
                />
                {productDetailsOpen && (
                  <UpCircleFilledInline
                    title="Click to collapse"
                    onClick={handleProductOpenChange}
                  />
                )}
                {!productDetailsOpen && (
                  <DownCircleOutlinedInline
                    title="Click to expand"
                    onClick={handleProductOpenChange}
                  />
                )}
              </ButtonGroup>
            </LineItemTitle>

            <FormItem
              name={namePrefix + fields.lineItems.id.path}
              defaultValue={item.id ? item.id : lineItem.id}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>
            <AutoCompleteFormItem
              label={fields.lineItems.containerType.label}
              name={namePrefix + fields.lineItems.containerType.path}
              alias={aliasPrefix + fields.lineItems.containerType.path}
              inputRef={firstInputRef}
              defaultValue={
                item.containerType ? item.containerType : lineItem.containerType
              }
              onBlur={dataChange}
              onChange={dataChange}
              onFocus={handleFocus}
              children={<TextArea autoSize />}
              id={`wasteLineItemContainerType_${index}`}
              simpleDataValidationRules={CheckContainerType}
            />

            <KnownContainerTypePresetSelect
              onChange={handleKnownContainerTypeSelectChange}
              namePrefix={namePrefix}
              index={index}
              defaultValue={
                item.knownContainerType
                  ? item.knownContainerType
                  : lineItem.knownContainerType
              }
            />

            <AutoCompleteFormItem
              label={fields.lineItems.wasteCode.label}
              name={namePrefix + fields.lineItems.wasteCode.path}
              alias={aliasPrefix + fields.lineItems.wasteCode.path}
              defaultValue={
                item.wasteCode ? item.wasteCode : lineItem.wasteCode
              }
              onBlur={handleWasteCodeChange}
              onChange={dataChange}
              onFocus={handleFocus}
              rules={Rules.ValidEWCCode}
              id={`wasteLineItemWasteCode_${index}`}
            />

            <FormRowWithValidation>
              <FormItem
                label={fields.lineItems.volumeEstimated.label}
                name={namePrefix + fields.lineItems.volumeEstimated.path}
                defaultValue={
                  item.volumeEstimated
                    ? item.volumeEstimated
                    : lineItem.volumeEstimated
                }
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemEstVol_${index}`}
                disabled={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={!isImperial ? 'm3' : 'cu yd'}
                />
              </FormItem>

              <FormItem
                label={fields.lineItems.volume.label}
                name={namePrefix + fields.lineItems.volume.path}
                defaultValue={item.volume ? item.volume : lineItem.volume}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemVol_${index}`}
                simpleDataValidationRules={v =>
                  CheckVolume(v, lineItem.volumeUoM)
                }
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={namePrefix + fields.lineItems.volumeUoM.path}
                      currentValue={lineItem.volumeUoM}
                      allValues={
                        !isImperial
                          ? METRIC_VOLUME_UNITS
                          : IMPERIAL_VOLUME_UNITS
                      }
                      onChange={dataChange}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>

            <FormRowWithValidation>
              <FormItem
                label={fields.lineItems.weightEstimated.label}
                name={namePrefix + fields.lineItems.weightEstimated.path}
                defaultValue={
                  item.weightEstimated
                    ? item.weightEstimated
                    : lineItem.weightEstimated
                }
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemEstWeight_${index}`}
                disabled={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={!isImperial ? 't' : 'lbs'}
                />
              </FormItem>

              <FormItem
                label={fields.lineItems.weight.label}
                name={namePrefix + fields.lineItems.weight.path}
                defaultValue={item.weight ? item.weight : lineItem.weight}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemWeight_${index}`}
                simpleDataValidationRules={v =>
                  CheckWeight(v, lineItem.weightUoM)
                }
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={namePrefix + fields.lineItems.weightUoM.path}
                      currentValue={lineItem.weightUoM}
                      allValues={
                        !isImperial
                          ? METRIC_WEIGHT_UNITS
                          : IMPERIAL_WEIGHT_UNITS
                      }
                      onChange={dataChange}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>

            <FormItem
              label={fields.lineItems.quantity.label}
              name={namePrefix + fields.lineItems.quantity.path}
              defaultValue={
                // Additional check because quantity defaults to 1
                item.quantity === 1 && lineItem.quantity
                  ? lineItem.quantity
                  : item.quantity
                    ? item.quantity
                    : lineItem.quantity
              }
              rules={Rules.NonNegativeNumber}
              onChange={dataChange}
              onFocus={handleFocus}
              id={`wasteLineItemQuantity_${index}`}
              simpleDataValidationRules={CheckQuantity}
            >
              <Input type="number" min="0" step="any" />
            </FormItem>
          </details>
        )}
      </Section>
      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isDuplicateItemsMultipleOpen}
        onClose={closeDuplicateItemMultipleModal}
        onSubmit={val => {
          duplicateMultiple(val)
        }}
        duplicateLineItemName={item.containerType}
      />
    </FormProvider>
  )
}

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
`
export const UpCircleFilledInline = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const DownCircleOutlinedInline = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
